import { EntityResolved } from '@api';
import { useRowsSelection } from '@terminal/hooks/use-rows-selection/useRowsSelection';
import { useWatchlistActions } from '@terminal/hooks/use-watchlist-actions/useWatchlistActions';
import { WatchlistError } from '@terminal/stores/use-watchlist/watchlist-types';
import { useOnboardingStore } from '@terminal/views/onboarding/hooks/useOnboardingStore';
import { useCallback, useRef, useState } from 'react';

import { useListNameEditor } from './useListNameEditor';
import { prependEntitiesDedupedByTag, validateListCreation } from './utils';

const ENTITY_ADDED_NOTIFICATION_DURATION_MS = 1000;

export const useWatchlistForm = () => {
  const [countEntitiesAdded, setCountEntitiesAdded] = useState(0);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const countEntitiesAddedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    clearStore,
    setAllDuplicateTickers,
    addManualEntityTags,
    manualEntityTags,
  } = useOnboardingStore(state => ({
    entitiesList: state.entitiesList,
    unMappedEntitiesList: state.unMappedEntitiesList,
    resolvedEntityData: state.resolvedEntityData,
    allDuplicateTickers: state.allDuplicateTickers,
    setResolvedEntityData: state.setResolvedEntityData,
    setEntitiesList: state.setEntitiesList,
    setUnMappedEntitiesList: state.setUnMappedEntitiesList,
    clearStore: state.clearStore,
    setAllDuplicateTickers: state.setAllDuplicateTickers,
    addManualEntityTags: state.addManualEntityTags,
    manualEntityTags: state.manualEntityTags,
  }));

  const {
    watchlistError,
    watchlistErrorText,
    createListLoading,
    hasExceededUsageLimit,
    createNewList,
    setWatchlistError,
  } = useWatchlistActions();

  const {
    listName,
    isRenameActive,
    setIsRenameActive,
    onUpdateName,
    resetName,
  } = useListNameEditor({
    onNameChange: () => setWatchlistError(undefined),
  });

  const {
    rowSelection,
    setRowSelection,
    updateRowSelection,
    toggleAllRowsSelection,
  } = useRowsSelection(entitiesList);

  const clearEntityAddedTimeout = useCallback(() => {
    if (countEntitiesAddedTimeoutRef.current) {
      clearTimeout(countEntitiesAddedTimeoutRef.current);
    }
  }, []);

  const entityAddedClear = useCallback(
    (count: number) => {
      setCountEntitiesAdded(count);
      clearEntityAddedTimeout();
      countEntitiesAddedTimeoutRef.current = setTimeout(
        () => setCountEntitiesAdded(0),
        ENTITY_ADDED_NOTIFICATION_DURATION_MS
      );
    },
    [clearEntityAddedTimeout]
  );

  const updateEntitiesList = useCallback(
    ({
      entitiesList: currentList,
      newEntities,
    }: {
      entitiesList: EntityResolved[];
      newEntities?: EntityResolved[];
    }) => {
      if (newEntities?.length) {
        addManualEntityTags(newEntities.map(e => e.tag));
        setEntitiesList(prev => prependEntitiesDedupedByTag(newEntities, prev));
        setRowSelection(prev => {
          const updatedRowSelection = { ...prev };
          newEntities.forEach(e => {
            updatedRowSelection[e.tag] = true;
          });
          return updatedRowSelection;
        });
      } else {
        setEntitiesList(currentList);
      }
    },
    [setEntitiesList, setRowSelection, addManualEntityTags]
  );

  const validateForm = useCallback(
    ({
      isAddMode,
      isEditMode,
    }: {
      isAddMode: boolean;
      isEditMode: boolean;
    }) => {
      return validateListCreation({
        isAddMode,
        isEditMode,
        listName,
        rowSelection,
        unMappedEntitiesList,
      });
    },
    [listName, rowSelection, unMappedEntitiesList]
  );

  const handleValidationError = useCallback(
    (error?: WatchlistError) => {
      if (error) {
        setWatchlistError(error);
      }

      if (error === WatchlistError.EmptyName) {
        resetName();
        setIsRenameActive(true);
      }
    },
    [setWatchlistError, resetName, setIsRenameActive]
  );

  const onToggleAllRows = useCallback(
    (selected: boolean) => {
      setWatchlistError(undefined);
      toggleAllRowsSelection(selected);
    },
    [setWatchlistError, toggleAllRowsSelection]
  );

  const onRowSelectionChange = useCallback(
    (id: string, isSelected: boolean) => {
      setWatchlistError(undefined);
      updateRowSelection(id, isSelected);
    },
    [setWatchlistError, updateRowSelection]
  );

  const hasUnsavedChanges = !!listName || !!entitiesList.length;
  const hasEntities = !!entitiesList.length;
  const selectedEntitiesCount =
    Object.values(rowSelection).filter(Boolean).length;

  return {
    // State
    listName,
    isRenameActive,
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    rowSelection,
    manualEntityTags,
    countEntitiesAdded,
    savingInProgress,
    // Error state
    watchlistError,
    watchlistErrorText,
    hasExceededUsageLimit,
    // Loading state
    createListLoading,
    // Computed
    hasUnsavedChanges,
    hasEntities,
    selectedEntitiesCount,
    // Actions
    setIsRenameActive,
    onUpdateName,
    resetName,
    setSavingInProgress,
    setWatchlistError,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    setAllDuplicateTickers,
    clearStore,
    updateEntitiesList,
    entityAddedClear,
    clearEntityAddedTimeout,
    validateForm,
    handleValidationError,
    onToggleAllRows,
    onRowSelectionChange,
    createNewList,
    setRowSelection,
  };
};
